import { useEffect, useRef, useState } from 'react'
import { api, ApiError, type UserbotAuthStatusResponse } from '@/lib/api'

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message)
      if (parsed && typeof parsed.detail === 'string') return parsed.detail
    } catch {
      /* not JSON */
    }
    if (err.message) return err.message
  }
  return fallback
}

export function ConnectUserbotModal({
  isOpen,
  onClose,
  onConnected,
}: {
  isOpen: boolean
  onClose: () => void
  onConnected: () => void
}) {
  const [requestId, setRequestId] = useState<number | null>(null)
  const [statusData, setStatusData] = useState<UserbotAuthStatusResponse | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const requestIdRef = useRef<number | null>(null)

  const TERMINAL = ['success', 'error', 'expired', 'cancelled']

  function stopPolling() {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setStatusData(null)
    setStartError(null)
    setRequestId(null)
    requestIdRef.current = null

    api.startUserbotQrAuth()
      .then((res) => {
        if (cancelled) return
        setRequestId(res.request_id)
        requestIdRef.current = res.request_id
      })
      .catch((err) => {
        if (cancelled) return
        setStartError(errorMessage(err, 'Не удалось начать подключение'))
      })

    return () => {
      cancelled = true
      stopPolling()
    }
  }, [isOpen])

  useEffect(() => {
    if (requestId === null) return
    stopPolling()
    const tick = () => {
      api.getUserbotQrStatus(requestId)
        .then((data) => {
          setStatusData(data)
          if (TERMINAL.includes(data.status)) {
            stopPolling()
            if (data.status === 'success') onConnected()
          }
        })
        .catch(() => {
          /* transient - keep polling */
        })
    }
    tick()
    pollRef.current = setInterval(tick, 2500)
    return () => stopPolling()
  }, [requestId])

  function handleClose() {
    const id = requestIdRef.current
    const status = statusData?.status
    if (id !== null && status && !TERMINAL.includes(status)) {
      void api.cancelUserbotQrAuth(id).catch(() => {})
    }
    stopPolling()
    onClose()
  }

  if (!isOpen) return null

  const status = statusData?.status
  const isStarting = !startError && (requestId === null || status === 'requested' || statusData === null)
  const isPending = status === 'qr_pending'
  const isSuccess = status === 'success'
  const failMessage =
    startError ??
    (status === 'error' || status === 'expired'
      ? statusData?.error ?? (status === 'expired' ? 'QR-код истёк' : 'Ошибка подключения')
      : null)

  return (
    <div
      role="dialog"
      aria-modal
      aria-label="Подключить юзербот"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(11,15,20,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: '24px',
        width: 'min(420px, 100%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, color: 'var(--ink)' }}>
            Подключить юзербот
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Закрыть"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 20, lineHeight: 1 }}
          >
            x
          </button>
        </div>

        {isStarting && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0' }}>
            <div className="ds-chat__loading__spin" style={{ width: 28, height: 28 }} />
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Готовим QR-код...</span>
          </div>
        )}

        {isPending && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            {statusData?.qr_image_b64 ? (
              <img
                src={`data:image/png;base64,${statusData.qr_image_b64}`}
                alt="QR-код для входа"
                style={{ width: 220, height: 220, borderRadius: 12, border: '1px solid var(--border)' }}
              />
            ) : (
              <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="ds-chat__loading__spin" style={{ width: 28, height: 28 }} />
              </div>
            )}
            <ol style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0, paddingLeft: 18 }}>
              <li>Откройте Telegram на телефоне</li>
              <li>Настройки - Устройства - Подключить устройство</li>
              <li>Наведите камеру на этот QR-код</li>
            </ol>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Ожидаем сканирование...</span>
          </div>
        )}

        {isSuccess && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '16px 0' }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>Юзербот подключен</span>
            <button type="button" className="btn btn--primary btn--sm" onClick={handleClose}>Готово</button>
          </div>
        )}

        {failMessage && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
            <span style={{ fontSize: 13, color: 'oklch(0.55 0.18 25)', textAlign: 'center', lineHeight: 1.5 }}>
              {failMessage}
            </span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleClose}>Закрыть</button>
          </div>
        )}
      </div>
    </div>
  )
}
